import { GrowthDashboardMidCardInfo } from '@/api/growth/dashboard/type';
import Tooltip from '@components/common/Tooltip';

interface GrowthDashboardProgCardProps {
    cardInfo: GrowthDashboardMidCardInfo | null;
    displayType: number;
}

interface StressCardConfig {
    title: string;
    value: number;
    level: string;
    color: string;
    icon: string;
    description?: string;
    showProgress: boolean;
}

const GrowthDashboardProgCard = ({
    cardInfo,
    displayType,
}: GrowthDashboardProgCardProps) => {
    if (!cardInfo) {
        return null;
    }

    /**
     * 스트레스 레벨 판정 함수 (DSSAT 기준)
     * 0 = 정상(스트레스 없음)
     * 1 = 최대 스트레스
     * 0~0.3: 적정(정상 범위)
     * 0.3~0.7: 과다(주의 필요)
     * 0.7~1.0: 심각(위험 수준)
     */
    const getStressLevel = (value: number): string => {
        if (value >= 0 && value <= 0.3) {
            return '적정';
        }
        if (value > 0.3 && value <= 0.7) {
            return '과다';
        }
        if (value > 0.7) {
            return '심각';
        }
        return '알수없음';
    };

    /**
     * VPD(수증기압차) 레벨 판정 함수
     * 기준: kPa 단위
     * < 0.5: 낮음 (질병 위험)
     * 0.5~1.2: 적정 (최적 범위)
     * > 1.2: 높음 (수분 스트레스)
     */
    const getVpdLevel = (value: number): string => {
        if (value < 0.5) {
            return '낮음';
        }
        if (value >= 0.5 && value <= 1.2) {
            return '적정';
        }
        if (value > 1.2) {
            return '높음';
        }
        return '알수없음';
    };

    /**
     * 각 항목별 전문적인 도움말 반환
     */
    const getTooltipContent = (displayType: number): string => {
        switch (displayType) {
            case 1:
                return 'NSTRES(질소 스트레스 계수)는 작물의 질소 결핍 정도를 나타냅니다. 0은 질소가 충분한 상태, 1은 심각한 질소 결핍 상태를 의미합니다. 질소는 엽록소 생성과 단백질 합성에 필수적이며, 결핍 시 생육이 저하되고 잎이 황화됩니다.';
            case 2:
                return 'PSTRES2(인산 생육 스트레스)는 인산 부족으로 인한 생육 저해 정도를 나타냅니다. 인산은 뿌리 발달과 에너지 전달에 중요한 역할을 하며, 결핍 시 개화 및 결실이 지연되고 뿌리 발육이 불량해집니다.';
            case 3:
                return 'PSTRES1(인산 광합성 스트레스)는 인산 부족이 광합성 효율에 미치는 영향을 나타냅니다. 인산은 ATP 생성에 필수적이며, 부족할 경우 광합성 속도가 감소하여 전체적인 생산성이 저하됩니다.';
            case 4:
                return '과습 스트레스는 토양 내 수분이 과도하여 뿌리의 산소 공급이 제한되는 정도를 나타냅니다. 과습 상태가 지속되면 뿌리 호흡이 억제되어 뿌리 부패, 생육 저하, 병해 발생 위험이 증가합니다.';
            case 5:
                return 'WSGD(생육 수분 스트레스)는 토양 수분 부족이 작물의 생장에 미치는 영향을 측정합니다. 0은 수분이 충분한 상태, 1은 심각한 수분 스트레스 상태입니다. 수분 부족 시 세포 확장이 억제되어 생장이 둔화됩니다.';
            case 6:
                return 'WSPD(광합성 수분 스트레스)는 수분 부족이 광합성 활동에 미치는 영향을 나타냅니다. 수분 스트레스 시 기공이 닫혀 CO₂ 흡수가 감소하고 광합성 효율이 저하되어 동화산물 생산이 감소합니다.';
            case 7:
                return 'VPD(Vapor Pressure Deficit, 수증기압차)는 대기의 건조 정도를 나타내는 지표로, 작물의 증산 속도에 직접적인 영향을 미칩니다. 적정 범위(0.5~1.2 kPa)에서 작물의 양분 흡수와 생장이 최적화됩니다.';
            default:
                return '도움말이 표시될 예정';
        }
    };

    /**
     * VPD 설명 문구 반환
     */
    const getVpdDescription = (value: number): string => {
        if (value < 0.5) {
            return '작물의 수분 증발량이 감소합니다. 식물 표면에 맺힌 이슬로 인해 질병 문제가 발생할 수 있으니 유의해주세요';
        }
        if (value >= 0.5 && value <= 1.2) {
            return '작물의 수분 증발량이 적절한 상태입니다';
        }
        if (value > 1.2) {
            return '작물의 수분 증발량이 증가합니다. 작물이 마를 수 있으니 수분관리에 유의해주세요';
        }
        return '';
    };

    /**
     * 질소 스트레스 설명
     */
    const getNitrogenStressDescription = (value: number): string => {
        if (value >= 0 && value <= 0.3) {
            return '질소 공급이 적정합니다';
        }
        if (value > 0.3 && value <= 0.7) {
            return '질소 부족이 감지됩니다. 추비를 고려하세요';
        }
        if (value > 0.7) {
            return '심각한 질소 부족 상태입니다. 즉시 질소 비료를 시비하세요';
        }
        return '';
    };

    /**
     * 인산 스트레스 설명
     */
    const getPhosphorusStressDescription = (value: number): string => {
        if (value >= 0 && value <= 0.3) {
            return '인산 공급이 적정합니다';
        }
        if (value > 0.3 && value <= 0.7) {
            return '인산 부족이 감지됩니다. 인산질 비료를 고려하세요';
        }
        if (value > 0.7) {
            return '심각한 인산 부족 상태입니다. 즉시 인산질 비료를 시비하세요';
        }
        return '';
    };

    /**
     * 과습 스트레스 설명
     */
    const getWetStressDescription = (value: number): string => {
        if (value >= 0 && value <= 0.3) {
            return '토양 수분 상태가 적정합니다';
        }
        if (value > 0.3 && value <= 0.7) {
            return '토양에 과습 징후가 있습니다. 배수에 유의하세요';
        }
        if (value > 0.7) {
            return '심각한 과습 상태입니다. 즉시 배수 조치가 필요합니다';
        }
        return '';
    };

    /**
     * 수분 스트레스 설명
     */
    const getWaterStressDescription = (
        value: number,
        type: 'growth' | 'photosynthesis',
    ): string => {
        const prefix = type === 'growth' ? '생육' : '광합성';

        if (value >= 0 && value <= 0.3) {
            return `${prefix}에 필요한 수분이 충분합니다`;
        }
        if (value > 0.3 && value <= 0.7) {
            return `수분 부족으로 ${prefix}이 저해되고 있습니다. 관수를 고려하세요`;
        }
        if (value > 0.7) {
            return `심각한 수분 부족으로 ${prefix}이 크게 저해됩니다. 즉시 관수하세요`;
        }
        return '';
    };

    const getCardConfig = (): StressCardConfig | null => {
        switch (displayType) {
            case 1: {
                // 질소생육스트레스 (NSTRES)
                const nstresValue = cardInfo.nstres || 0;
                return {
                    title: '질소생육스트레스',
                    value: nstresValue,
                    level: getStressLevel(nstresValue),
                    color: 'primary',
                    icon: 'ri-bubble-chart-line',
                    description: getNitrogenStressDescription(nstresValue),
                    showProgress: true,
                };
            }

            case 2: {
                // 인산생육스트레스 (PSTRES2)
                const pstres2Value = cardInfo.pstres2 || 0;
                return {
                    title: '인산생육스트레스',
                    value: pstres2Value,
                    level: getStressLevel(pstres2Value),
                    color: 'pink',
                    icon: 'ri-bubble-chart-line',
                    description: getPhosphorusStressDescription(pstres2Value),
                    showProgress: true,
                };
            }

            case 3: {
                // 인산광합성스트레스 (PSTRES1)
                const pstres1Value = cardInfo.pstres1 || 0;
                return {
                    title: '인산광합성스트레스',
                    value: pstres1Value,
                    level: getStressLevel(pstres1Value),
                    color: 'secondary',
                    icon: 'ri-bubble-chart-line',
                    description: `인산 부족으로 인한 광합성 효율: ${pstres1Value <= 0.3 ? '정상' : pstres1Value <= 0.7 ? '저하' : '심각한 저하'}`,
                    showProgress: true,
                };
            }

            case 4: {
                // 과습 (WET_STRESS)
                const wetStressValue = cardInfo.wetStress || 0;
                return {
                    title: '과습스트레스',
                    value: wetStressValue,
                    level: getStressLevel(wetStressValue),
                    color: 'info',
                    icon: 'ri-bubble-chart-line',
                    description: getWetStressDescription(wetStressValue),
                    showProgress: true,
                };
            }

            case 5: {
                // 생육수분스트레스 (WATER_STRESS / WSGD)
                const waterStressValue = cardInfo.waterStress || 0;
                return {
                    title: '생육수분스트레스',
                    value: waterStressValue,
                    level: getStressLevel(waterStressValue),
                    color: 'success',
                    icon: 'ri-bubble-chart-line',
                    description: getWaterStressDescription(
                        waterStressValue,
                        'growth',
                    ),
                    showProgress: true,
                };
            }

            case 6: {
                // 광합성수분스트레스 (WATER_PS_STRESS / WSPD)
                const waterPsStressValue = cardInfo.waterPsStress || 0;
                return {
                    title: '광합성수분스트레스',
                    value: waterPsStressValue,
                    level: getStressLevel(waterPsStressValue),
                    color: 'danger',
                    icon: 'ri-bubble-chart-line',
                    description: getWaterStressDescription(
                        waterPsStressValue,
                        'photosynthesis',
                    ),
                    showProgress: true,
                };
            }

            case 7: {
                // VPD - 수증기압차
                const vpdValue = cardInfo.vpd || 0;
                const vpdLevel = getVpdLevel(vpdValue);
                return {
                    title: 'VPD - 수증기압차',
                    value: vpdValue,
                    level: vpdLevel,
                    color: 'info',
                    icon: 'ri-dashboard-3-line',
                    description: getVpdDescription(vpdValue),
                    showProgress: false,
                };
            }

            default:
                return null;
        }
    };

    const config = getCardConfig();

    if (!config) {
        return null;
    }

    // 퍼센트 계산 (0~1 범위를 0~100%로 변환)
    const progressPercent = Math.min(Math.round(config.value * 100), 100);

    return (
        <div className={`custom-card card hrm-main-card ${config.color}`}>
            <div className="card-body">
                <div className="d-flex align-items-center mb-3">
                    <div className="me-3">
                        <div
                            className={`avatar avatar-md rounded-4 bg-${config.color} shadow shadow-${config.color} text-fixed-white`}
                        >
                            <i className={`${config.icon} fs-18`}></i>
                        </div>
                    </div>
                    <div className="flex-fill">
                        <p className="mb-1 text-muted">
                            {config.title}
                            <Tooltip
                                content={getTooltipContent(displayType)}
                                customClass="tooltip-dark"
                                placement="top"
                            >
                                <i className="ri-question-line" />
                            </Tooltip>
                        </p>
                        <div className="d-flex align-items-top justify-content-between">
                            <h5 className="fw-semibold mb-0 lh-1">
                                {config.level}
                            </h5>
                            <div className={`text-${config.color} fw-semibold`}>
                                {config.value.toFixed(2)}
                            </div>
                        </div>
                    </div>
                </div>
                {config.showProgress ? (
                    <div
                        role="progressbar"
                        aria-valuenow={progressPercent}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        className="progress progress-lg p-1 progress-animate progress"
                    >
                        <div
                            role="progressbar"
                            className={`progress-bar progress-bar-striped bg-${config.color} progress-bar`}
                            aria-valuenow={progressPercent}
                            aria-valuemin={0}
                            aria-valuemax={100}
                            style={{ width: `${progressPercent}%` }}
                        ></div>
                    </div>
                ) : (
                    config.description && (
                        <div style={{ height: '42px' }}>
                            <small>{config.description}</small>
                        </div>
                    )
                )}
            </div>
        </div>
    );
};

export default GrowthDashboardProgCard;
